import { useEffect, useRef, useState } from "react";

const DEFAULT_TIMEOUT_MS = 15 * 60 * 1000;
const ACTIVITY_EVENTS = ["mousedown", "keydown", "touchstart", "scroll"] as const;

export function computeNextIdleAt(now: number, timeout: number = DEFAULT_TIMEOUT_MS): number {
  return now + timeout;
}

// `enabled: false` disables idle tracking entirely (the user-configurable
// "never auto-lock" option). When disabled we never flip `idle` to true.
export function useIdleTimer(
  timeoutMs: number = DEFAULT_TIMEOUT_MS,
  enabled: boolean = true,
) {
  const [idle, setIdle] = useState(false);
  const deadlineRef = useRef<number>(computeNextIdleAt(Date.now(), timeoutMs));

  useEffect(() => {
    if (!enabled) {
      setIdle(false);
      return;
    }
    const reset = () => {
      deadlineRef.current = computeNextIdleAt(Date.now(), timeoutMs);
      setIdle(false);
    };
    reset(); // re-anchor deadline when timeout/enabled changes
    for (const ev of ACTIVITY_EVENTS) window.addEventListener(ev, reset, { passive: true });
    const interval = window.setInterval(() => {
      if (Date.now() >= deadlineRef.current) setIdle(true);
    }, 5000);
    return () => {
      for (const ev of ACTIVITY_EVENTS) window.removeEventListener(ev, reset);
      window.clearInterval(interval);
    };
  }, [timeoutMs, enabled]);

  return {
    idle,
    reset: () => {
      deadlineRef.current = computeNextIdleAt(Date.now(), timeoutMs);
      setIdle(false);
    },
  };
}
